import { Button, Callout, HTMLSelect, Slider, Tab, Tabs, Tag } from '@blueprintjs/core'
import type { Intent } from '@blueprintjs/core'
import { useState } from 'react'

import type { Message, Store } from '../store'

/**
 * Collapsible bottom panel — Timeline and Messages tabs.
 *
 * Phase 1 wires the UI; Phase 5 streams real trajectory frames and
 * message events from the backend.
 */
export function BottomPanel({ store }: { store: Store }) {
  const [tab, setTab] = useState<'timeline' | 'messages'>('timeline')
  const nMessages = store.messages.length
  const nErrors = store.messages.filter((m) => m.intent === 'danger').length

  return (
    <div className="bottom-panel">
      <Tabs id="bottom-tabs" selectedTabId={tab} onChange={(id) => setTab(id as 'timeline' | 'messages')}>
        <Tab id="timeline" title="Timeline" />
        <Tab
          id="messages"
          title={
            <span>
              Messages{' '}
              {nMessages > 0 && (
                <Tag minimal intent={nErrors ? 'danger' : 'warning'} round>
                  {nMessages}
                </Tag>
              )}
            </span>
          }
        />
      </Tabs>
      {tab === 'timeline' ? <Timeline store={store} /> : <Messages store={store} />}
    </div>
  )
}

function Timeline({ store }: { store: Store }) {
  const total = store.totalFrames
  const disabled = total === 0
  // Operation-segment colours stacked under the slider to show which
  // path owns each range of frames.
  const segments = useOpSegments(store)
  return (
    <div className="timeline">
      <Button
        icon={store.playing ? 'pause' : 'play'}
        onClick={() => store.togglePlay()}
        disabled={disabled}
      />
      <Button icon="step-forward" onClick={() => store.stepFrame(1)} disabled={disabled} />
      <Button icon="step-backward" onClick={() => store.stepFrame(-1)} disabled={disabled} />
      <div className="timeline-slider-wrap">
        <div className="timeline-segments">
          {segments.map((s, i) => (
            <div
              key={i}
              className="timeline-segment"
              style={{
                left: `${(s.start / Math.max(1, total)) * 100}%`,
                width: `${((s.end - s.start) / Math.max(1, total)) * 100}%`,
                background: s.collided ? '#ef4444' : s.color,
              }}
              title={`${s.label}: frames ${s.start}–${s.end - 1}${
                s.collided ? ' (collides)' : ''
              }`}
            />
          ))}
        </div>
        <Slider
          min={0}
          max={Math.max(0, total - 1)}
          value={store.scrubFrame}
          onChange={(v) => store.setScrubFrame(v)}
          stepSize={1}
          labelStepSize={Math.max(1, Math.floor(total / 10))}
          disabled={disabled}
        />
      </div>
      <HTMLSelect
        options={['0.25x', '0.5x', '1x', '2x', '4x']}
        value={`${store.playSpeed}x`}
        onChange={(e) => store.setPlaySpeed(parseFloat(e.target.value) || 1)}
        disabled={disabled}
      />
      <span className="timeline-readout">
        frame {store.scrubFrame} / {Math.max(0, total - 1)}
      </span>
    </div>
  )
}

type Segment = { label: string; start: number; end: number; color: string; collided: boolean }

function useOpSegments(store: Store): Segment[] {
  // The backend returns a flat trajectory per operation; we concatenate
  // in operation order. Each operation's trajectory length lives on
  // op.trajectory.frames[] but might be unavailable. Fall back to
  // dividing totalFrames by the operation count.
  const ops = store.job.operations.filter((o) => !o.suppressed)
  if (ops.length === 0 || store.totalFrames === 0) return []
  const palette = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#14b8a6']
  const per = Math.max(1, Math.floor(store.totalFrames / ops.length))
  let cursor = 0
  return ops.map((op, i) => {
    const n = i === ops.length - 1 ? store.totalFrames - cursor : per
    const seg: Segment = {
      label: op.label,
      start: cursor,
      end: cursor + n,
      color: palette[i % palette.length],
      collided: !!op.collision && op.collision.collisionCount > 0,
    }
    cursor += n
    return seg
  })
}

function Messages({ store }: { store: Store }) {
  if (store.messages.length === 0) {
    return (
      <div className="messages-empty">
        <Callout>No reach / collision / joint issues reported yet.</Callout>
      </div>
    )
  }
  return (
    <div className="messages-list">
      {store.messages.map((m) => (
        <MessageRow key={m.id} m={m} store={store} />
      ))}
    </div>
  )
}

function MessageRow({ m, store }: { m: Message; store: Store }) {
  const intent: Intent = m.intent
  return (
    <div className="messages-row" onClick={() => store.jumpToMessage(m.id)}>
      <Tag intent={intent} minimal>
        {m.kind}
      </Tag>
      <span className="messages-row-body">{m.body}</span>
      <span className="messages-row-frame">frame {m.frame}</span>
    </div>
  )
}
